<!--
  The next-session card, above "Ask for help". It shows the member's next
  scheduled session in the coming week: who, and when. The card opens the
  session page. From 10 minutes before the start until the end, a Join button
  opens the call, the same link as "Join the room" on the session page. The
  card hides when the session ends. In the icon rail only the face shows, with
  a green ring while Join is open.
-->
<template>
  <div
    v-if="session && !hasEnded"
    class="gm-sb-next"
    :class="{ 'is-now': isJoinable }"
  >
    <InertiaLink
      v-tooltip="{ content: tooltip, placement: 'right', disabled: !isRail }"
      :href="session.href"
      class="gm-sb-next-main"
    >
      <img
        v-if="session.withAvatarUrl && !hasFaceFailed"
        :src="session.withAvatarUrl"
        alt=""
        class="gm-sb-next-face"
        @error="hasFaceFailed = true"
      />
      <CalendarIcon v-else class="gm-sb-next-face" aria-hidden="true" />
      <span class="gm-sb-next-body">
        <span class="gm-sb-next-tag">Next session</span>
        <span class="gm-sb-next-when">{{ whenText }}</span>
        <span v-if="session.withName" class="gm-sb-next-who">
          with {{ session.withName }}
        </span>
      </span>
    </InertiaLink>
    <a
      v-if="isJoinable && joinUrl"
      :href="joinUrl"
      target="_blank"
      rel="noopener"
      class="gm-sb-next-join"
    >
      <VideoIcon class="size-4" aria-hidden="true" />
      Join
    </a>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { DateTime } from 'luxon'
import CalendarIcon from '~icons/lucide/calendar-clock'
import VideoIcon from '~icons/lucide/video'
import { classifyCallLink } from '@/modules/sessions/composables/useJoinCall'

defineProps<{ isRail: boolean }>()

type NextSession = {
  id: number
  startsAt: string
  endsAt: string
  withName: string | null
  withAvatarUrl: string | null
  href: string
  communicationTool: string
  callLink: string | null
  internalCallLink: string
}

// Join opens this long before the start.
const JOIN_OPENS_MINUTES = 10
const TICK_MS = 30_000

const page = usePage()
// A photo that does not load shows the calendar icon, not a broken image.
const hasFaceFailed = ref(false)
const session = computed(
  () =>
    (page.props.shared as { sidebar?: { nextSession?: NextSession | null } })
      .sidebar?.nextSession ?? null,
)
watch(
  () => session.value?.id,
  () => {
    hasFaceFailed.value = false
  },
)

// The card changes by the clock: "Starts in 8 min", Join opens, the card
// hides at the end. One tick every 30 seconds is enough for minutes.
const now = ref(DateTime.now())
let timer: number | null = null
onMounted(() => {
  timer = window.setInterval(() => {
    now.value = DateTime.now()
  }, TICK_MS)
})
onBeforeUnmount(() => {
  if (timer !== null) window.clearInterval(timer)
})

const startsAt = computed(() =>
  session.value ? DateTime.fromISO(session.value.startsAt) : null,
)
const endsAt = computed(() =>
  session.value ? DateTime.fromISO(session.value.endsAt) : null,
)

const hasEnded = computed(() => !!endsAt.value && now.value >= endsAt.value)
const hasStarted = computed(
  () => !!startsAt.value && now.value >= startsAt.value,
)
const isJoinable = computed(
  () =>
    !!startsAt.value &&
    !hasEnded.value &&
    now.value >= startsAt.value.minus({ minutes: JOIN_OPENS_MINUTES }),
)

const joinUrl = computed(() => {
  if (!session.value) return ''
  return classifyCallLink(
    session.value.communicationTool,
    session.value.callLink,
    session.value.internalCallLink,
  ).url
})

// Luxon's default zone is the member's time zone (Layout.vue sets it).
const whenText = computed(() => {
  const start = startsAt.value
  if (!start || !endsAt.value) return ''
  if (hasStarted.value) return `Now, until ${endsAt.value.toFormat('t')}`
  if (isJoinable.value) {
    const minutes = Math.max(
      1,
      Math.ceil(start.diff(now.value, 'minutes').minutes),
    )
    return `Starts in ${minutes} min`
  }
  if (start.hasSame(now.value, 'day')) return `Today, ${start.toFormat('t')}`
  if (start.hasSame(now.value.plus({ days: 1 }), 'day')) {
    return `Tomorrow, ${start.toFormat('t')}`
  }
  return start.toFormat('ccc d LLL, t')
})

const tooltip = computed(() =>
  session.value?.withName
    ? `Next session: ${whenText.value}, with ${session.value.withName}`
    : `Next session: ${whenText.value}`,
)
</script>
